import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Logo from '../Logo';
import NotificationBell from './NotificationBell';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { settingsApi } from '../../services/api';

const primaryNavItems = [
  { label: 'Dashboard',  path: '/user-dashboard',    icon: 'LayoutDashboard' },
  { label: 'Portfolios', path: '/portfolio-overview', icon: 'Briefcase' },
  { label: 'Watchlist',  path: '/watchlist',          icon: 'Star' },
  { label: 'Calendar',   path: '/dividend-calendar',  icon: 'CalendarDays' },
  { label: 'Options',    path: '/options-tracker',    icon: 'TrendingUp', proOnly: true },
  { label: 'Reports',    path: '/income-reports',     icon: 'BarChart3' },
  { label: 'Brokerages', path: '/brokerage-sync',     icon: 'Building2' },
];

const moreNavItems = [
  { label: 'Alerts',           path: '/alerts-management',     icon: 'Bell' },
  { label: 'Ideas',            path: '/ideas',                 icon: 'Lightbulb' },
  { label: 'News',             path: '/news',                  icon: 'Newspaper' },
  { label: 'Earn — Refer Friends', path: '/affiliates/dashboard', icon: 'DollarSign' },
  { label: 'Admin Dashboard',  path: '/admin-dashboard',       icon: 'ShieldCheck',  adminOnly: true },
  { label: 'User Management',  path: '/admin-users',           icon: 'Users',        adminOnly: true },
  { label: 'Email Panel',      path: '/admin-email-panel',     icon: 'Mail',         adminOnly: true },
  { label: 'Delivery Tracker', path: '/email-delivery-tracker',icon: 'BarChart2',    adminOnly: true },
  { label: 'Plan Editor',      path: '/admin-plans',           icon: 'CreditCard',   adminOnly: true },
  { label: 'Blog Posts',       path: '/admin-blog',            icon: 'FileText',     adminOnly: true },
  { label: 'Affiliate Program',path: '/admin-affiliates',      icon: 'Users2',       adminOnly: true },
];

export default function Header() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { settings, updateSettings } = useSettings();
  const isAdmin = user?.role === 'admin';
  const isDark = settings?.theme === 'dark' || (settings?.theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const moreRef = useRef(null);
  const accountRef = useRef(null);

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    updateSettings({ theme: next });
    settingsApi?.updateAll({ theme: next })?.catch(() => {});
  };

  const isActive = (path) => location?.pathname === path;

  const isPro = user?.plan === 'pro' || user?.plan === 'complimentary';

  const visiblePrimaryItems = primaryNavItems?.filter(
    (item) => !item?.proOnly || isPro
  );

  const visibleMoreItems = moreNavItems?.filter(
    (item) => !item?.adminOnly || isAdmin
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreRef?.current && !moreRef?.current?.contains(e?.target)) setMoreOpen(false);
      if (accountRef?.current && !accountRef?.current?.contains(e?.target)) setAccountOpen(false);
    };
    const handleEsc = (e) => {
      if (e?.key === 'Escape') {
        setMoreOpen(false);
        setAccountOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMoreOpen(false);
    setAccountOpen(false);
  }, [location?.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <header className="nav-primary px-4 lg:px-6">
        <div className="flex items-center w-full gap-2">
          {/* Logo */}
          <Link
            to="/user-dashboard"
            className="flex items-center gap-2.5 flex-shrink-0 focus-ring rounded-md"
            aria-label="DividendTrack Home"
          >
            <Logo size={32} />
            <span
              className="font-heading font-semibold text-lg hidden sm:block"
              style={{ color: 'var(--color-foreground)' }}
            >
              DividendTrack
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav
            className="hidden lg:flex items-center gap-1 ml-6 flex-1"
            aria-label="Primary navigation"
          >
            {visiblePrimaryItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`nav-item focus-ring ${isActive(item?.path) ? 'active' : ''}`}
                aria-current={isActive(item?.path) ? 'page' : undefined}
              >
                <Icon name={item?.icon} size={16} />
                {item?.label}
              </Link>
            ))}

            {/* More dropdown */}
            {visibleMoreItems?.length > 0 && (
              <div className="relative" ref={moreRef}>
                <button
                  onClick={() => setMoreOpen((v) => !v)}
                  className={`nav-item focus-ring ${visibleMoreItems?.some((i) => isActive(i?.path)) ? 'active' : ''}`}
                  aria-expanded={moreOpen}
                  aria-haspopup="true"
                >
                  <Icon name="MoreHorizontal" size={16} />
                  More
                  <Icon name={moreOpen ? 'ChevronUp' : 'ChevronDown'} size={14} />
                </button>
                {moreOpen && (
                  <div className="dropdown-menu absolute top-full left-0 mt-2 w-48 py-1">
                    {visibleMoreItems?.map((item) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm font-caption transition-colors hover:bg-muted ${
                          isActive(item?.path)
                            ? 'text-primary font-medium' :'text-foreground'
                        }`}
                        style={{ fontFamily: 'var(--font-caption)' }}
                      >
                        <Icon name={item?.icon} size={15} />
                        {item?.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2 ml-auto">
            {/* Upgrade / Premium / Complimentary badge */}
            {user?.plan === 'pro' || user?.plan === 'complimentary' ? (
              <Link
                to="/subscription"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ backgroundColor: 'rgba(124,58,237,0.12)', color: '#7C3AED', fontFamily: 'var(--font-caption)' }}
              >
                <Icon name="Star" size={12} color="#7C3AED" />
                Pro
              </Link>
            ) : user?.plan === 'premium' ? (
              <Link
                to="/subscription"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ backgroundColor: 'rgba(27,94,32,0.15)', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}
              >
                <Icon name="Star" size={12} color="var(--color-primary)" />
                Premium
              </Link>
            ) : user && (
              <Link
                to="/subscription"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all hover:opacity-90"
                style={{ backgroundColor: '#D97706', color: 'white', fontFamily: 'var(--font-caption)' }}
              >
                <Icon name="Zap" size={12} color="white" />
                Upgrade
              </Link>
            )}

            {/* Dark / Light toggle */}
            <button
              onClick={toggleTheme}
              className="flex items-center justify-center w-9 h-9 rounded-md transition-colors hover:bg-muted focus-ring"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{ transition: 'all var(--transition-base)' }}
            >
              <Icon name={isDark ? 'Sun' : 'Moon'} size={18} color="var(--color-muted-foreground)" />
            </button>

            {/* Notification Bell */}
            <NotificationBell />

            {/* Account Menu */}
            <div className="relative" ref={accountRef}>
              <button
                onClick={() => setAccountOpen((v) => !v)}
                className="flex items-center gap-1.5 px-2 py-2 rounded-md focus-ring transition-colors hover:bg-muted"
                aria-expanded={accountOpen}
                aria-haspopup="true"
                aria-label="Account menu"
                style={{ transition: 'all var(--transition-base)' }}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
                  style={{
                    backgroundColor: 'var(--color-primary)',
                    color: 'var(--color-primary-foreground)',
                    fontFamily: 'var(--font-caption)',
                  }}
                >
                  {[user?.first_name, user?.last_name].filter(Boolean).map(n => n[0].toUpperCase()).join('') || 'U'}
                </div>
                <Icon
                  name={accountOpen ? 'ChevronUp' : 'ChevronDown'}
                  size={14}
                  color="var(--color-muted-foreground)"
                />
              </button>

              {accountOpen && (
                <div className="dropdown-menu absolute top-full right-0 mt-2 w-56 py-1">
                  <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
                    <p
                      className="text-sm font-semibold"
                      style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}
                    >
                      {user?.name || 'Account'}
                    </p>
                    <p
                      className="text-xs mt-0.5"
                      style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}
                    >
                      {user?.email || ''}
                    </p>
                    <span
                      className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded text-xs font-medium"
                      style={{
                        backgroundColor: user?.plan === 'free' || !user?.plan
                          ? 'rgba(107,114,128,0.12)'
                          : 'rgba(27, 94, 32, 0.1)',
                        color: user?.plan === 'free' || !user?.plan
                          ? 'var(--color-muted-foreground)'
                          : 'var(--color-primary)',
                        fontFamily: 'var(--font-caption)',
                      }}
                    >
                      {user?.plan !== 'free' && user?.plan && <Icon name="Star" size={10} />}
                      {user?.plan === 'pro'           ? 'Pro'
                        : user?.plan === 'complimentary' ? 'Pro'
                        : user?.plan === 'premium'       ? 'Premium'
                        : 'Free'}
                    </span>
                  </div>
                  <div className="py-1">
                    <Link
                      to="/settings"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-muted"
                      style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}
                    >
                      <Icon name="Settings" size={15} />
                      Settings
                    </Link>
                    <Link
                      to="/subscription"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-muted"
                      style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}
                    >
                      <Icon name="CreditCard" size={15} />
                      Subscription
                    </Link>
                    {isAdmin && (
                      <>
                        <div className="px-4 pt-2 pb-1">
                          <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                            Admin
                          </p>
                        </div>
                        <Link to="/admin-dashboard" className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-muted"
                          style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>
                          <Icon name="ShieldCheck" size={15} />
                          Admin Dashboard
                        </Link>
                        <Link to="/admin-users" className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-muted"
                          style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>
                          <Icon name="Users" size={15} />
                          User Management
                        </Link>
                        <Link to="/admin-email-panel" className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-muted"
                          style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>
                          <Icon name="Mail" size={15} />
                          Email Panel
                        </Link>
                        <Link to="/email-delivery-tracker" className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-muted"
                          style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>
                          <Icon name="BarChart2" size={15} />
                          Delivery Tracker
                        </Link>
                        <Link to="/admin-plans" className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-muted"
                          style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>
                          <Icon name="CreditCard" size={15} />
                          Plan Editor
                        </Link>
                        <Link to="/admin-blog" className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-muted"
                          style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>
                          <Icon name="FileText" size={15} />
                          Blog Posts
                        </Link>
                      </>
                    )}
                  </div>
                  <div className="border-t py-1" style={{ borderColor: 'var(--color-border)' }}>
                    <button
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm transition-colors hover:bg-muted text-left"
                      style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-error)' }}
                      onClick={logout}
                    >
                      <Icon name="LogOut" size={15} color="var(--color-error)" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-md focus-ring transition-colors hover:bg-muted"
              onClick={() => setMobileOpen((v) => !v)}
              aria-expanded={mobileOpen}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              style={{ transition: 'all var(--transition-base)' }}
            >
              <Icon name={mobileOpen ? 'X' : 'Menu'} size={20} color="var(--color-foreground)" />
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="mobile-menu lg:hidden" role="dialog" aria-modal="true" aria-label="Mobile navigation">
          {/* Mobile header */}
          <div
            className="flex items-center justify-between px-4 h-16 border-b flex-shrink-0"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <Link
              to="/user-dashboard"
              className="flex items-center gap-2.5 focus-ring rounded-md"
              onClick={() => setMobileOpen(false)}
            >
              <Logo size={32} />
              <span
                className="font-heading font-semibold text-lg"
                style={{ color: 'var(--color-foreground)' }}
              >
                DividendTrack
              </span>
            </Link>
            <button
              className="flex items-center justify-center w-10 h-10 rounded-md focus-ring hover:bg-muted"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
              style={{ transition: 'all var(--transition-base)' }}
            >
              <Icon name="X" size={20} color="var(--color-foreground)" />
            </button>
          </div>

          {/* Mobile nav items */}
          <nav className="px-4 py-4" aria-label="Mobile navigation">
            <p
              className="text-xs font-semibold uppercase tracking-wider mb-3 px-3"
              style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}
            >
              Navigation
            </p>
            {visiblePrimaryItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center gap-3 px-3 py-3 rounded-md mb-1 transition-colors ${
                  isActive(item?.path)
                    ? 'text-primary font-medium' :'text-foreground hover:bg-muted'
                }`}
                style={{
                  fontFamily: 'var(--font-caption)',
                  fontSize: '0.9375rem',
                  backgroundColor: isActive(item?.path) ? 'rgba(27, 94, 32, 0.08)' : undefined,
                  transition: 'all var(--transition-base)',
                }}
                aria-current={isActive(item?.path) ? 'page' : undefined}
              >
                <Icon
                  name={item?.icon}
                  size={18}
                  color={isActive(item?.path) ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                />
                {item?.label}
              </Link>
            ))}

            {visibleMoreItems?.length > 0 && (
              <>
                <div
                  className="my-4 border-t"
                  style={{ borderColor: 'var(--color-border)' }}
                />
                <p
                  className="text-xs font-semibold uppercase tracking-wider mb-3 px-3"
                  style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}
                >
                  More
                </p>
                {visibleMoreItems?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`flex items-center gap-3 px-3 py-3 rounded-md mb-1 transition-colors ${
                      isActive(item?.path)
                        ? 'text-primary font-medium' :'text-foreground hover:bg-muted'
                    }`}
                    style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.9375rem',
                      backgroundColor: isActive(item?.path) ? 'rgba(27, 94, 32, 0.08)' : undefined,
                      transition: 'all var(--transition-base)',
                    }}
                    aria-current={isActive(item?.path) ? 'page' : undefined}
                  >
                    <Icon
                      name={item?.icon}
                      size={18}
                      color={isActive(item?.path) ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                    />
                    {item?.label}
                  </Link>
                ))}
              </>
            )}
          </nav>

          {/* Mobile account section */}
          <div
            className="absolute bottom-0 left-0 right-0 border-t px-4 py-4"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-card)' }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-primary-foreground)',
                  fontFamily: 'var(--font-caption)',
                }}
              >
                {[user?.first_name, user?.last_name].filter(Boolean).map(n => n[0].toUpperCase()).join('') || 'U'}
              </div>
              <div>
                <p
                  className="text-sm font-semibold"
                  style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}
                >
                  {user?.name || 'Account'}
                </p>
                <p
                  className="text-xs"
                  style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}
                >
                  {user?.email || ''}
                </p>
              </div>
            </div>
            <button
              className="flex items-center gap-2 w-full px-3 py-2.5 rounded-md text-sm transition-colors hover:bg-muted"
              style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-error)' }}
              onClick={logout}
            >
              <Icon name="LogOut" size={16} color="var(--color-error)" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </>
  );
}